import { Tooltip } from 'antd'
import { OntologyValueType } from 'features/dynamicOntology'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import {
  getFieldSource,
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  FieldValue,
  FieldValues,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormSection } from 'features/libra/sections/sectionTypes'
import { isAiUnextracted } from 'features/libra/stepComponents/GongAgentAutofill'
import { useComponentScope } from 'features/libra/workspace/ComponentScopeContext'
import { RecursivePropertyView } from 'features/objectViews'
import pick from 'lodash/pick'
import pickBy from 'lodash/pickBy'
import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { ExternalValueLookup } from './ExternalValueLookup'
import {
  DocumentSourcePopover,
  ReadOnlySourceDisplay,
  SourceInput,
} from './SourceInput'

interface ConditionalFormSectionProps {
  config: FormSection
  ontologyFields: Record<string, OntologyField>
  virtualObject: FieldValues
  setVirtualObject?: Dispatch<SetStateAction<FieldValues>>
  stepId: string
}

export const ConditionalFormSectionView: FC<ConditionalFormSectionProps> = ({
  config,
  ontologyFields,
  virtualObject,
  setVirtualObject,
  stepId,
}) => {
  const sectionFieldValues = virtualObject ?? {}
  const isReadonly = !setVirtualObject
  // When rendered inside `<UnitComponentTabs>`, tag every field/cell anchor
  // with the active `componentId` so two threads on the same `fieldId` under
  // two different component tabs stay distinct. Outside of tabs, `scope` is
  // `null` and anchors omit the key.
  const componentScope = useComponentScope()
  const activeComponentId = componentScope?.componentId

  const filteredOntologyFields = useMemo<Record<string, InputField>>(
    () =>
      pickBy(
        pick(ontologyFields, config.fields),
        (field) => field?.category === FieldCategory.INPUT
      ) as Record<string, InputField>,
    [config.fields, ontologyFields]
  )

  const fieldEntries = useMemo(
    () =>
      Object.entries(filteredOntologyFields).map(([fullKey, fieldDef]) => ({
        fullKey,
        shortKey: fullKey.split('.')[0],
        fieldDef,
      })),
    [filteredOntologyFields]
  )

  const createFieldSetter = useCallback(
    (
      fullKey: string
    ): Dispatch<SetStateAction<OntologyValueType | undefined>> | undefined => {
      if (!setVirtualObject) return undefined
      return (valOrUpdater) => {
        setVirtualObject((prev) => {
          const currentValue = unwrapFieldValue(prev[fullKey])
          const resolved =
            typeof valOrUpdater === 'function'
              ? (
                  valOrUpdater as (
                    p: OntologyValueType | undefined
                  ) => OntologyValueType | undefined
                )(currentValue as OntologyValueType | undefined)
              : valOrUpdater
          const existingSource = getFieldSource(prev[fullKey])
          return {
            ...prev,
            [fullKey]: wrapFieldValue(resolved, existingSource),
          }
        })
      }
    },
    [setVirtualObject]
  )

  return (
    <SectionContainer>
      {fieldEntries.map(({ fullKey, shortKey, fieldDef }) => {
        // Cast once to FieldValue | undefined to avoid the `?? {}` fallback
        // widening sectionFieldValues to `FieldValues | {}` which makes
        // string-index access resolve to `unknown`.
        const fieldValue: FieldValue | undefined = (
          sectionFieldValues as FieldValues
        )[fullKey]
        const unextracted = isAiUnextracted(fieldValue)
        return (
          <CommentIndicator
            key={fullKey}
            anchor={{
              type: 'field',
              stepId,
              fieldId: fullKey,
              ...(activeComponentId && { componentId: activeComponentId }),
            }}>
            <Tooltip
              title={
                unextracted
                  ? 'AI could not find data for this field in the available documents'
                  : undefined
              }>
              <UnextractedWrapper $unextracted={unextracted}>
                <FormFieldPropertyView
                  label={fieldDef.name}
                  propertyKey={shortKey}
                  object={unwrapFieldValue(fieldValue) as OntologyValueType}
                  setObject={createFieldSetter(fullKey)}
                  fieldDef={fieldDef}
                  leafActions={({ object, setObject, fieldDef: fd }) => {
                    const source = getFieldSource(fieldValue)
                    const hasIncludeSource =
                      fd && 'includeSource' in fd && fd.includeSource

                    if (isReadonly) {
                      return (
                        <ActionRow>
                          {hasIncludeSource && source !== null ? (
                            <DocumentSourcePopover source={source}>
                              <ReadOnlySourceDisplay source={source} />
                            </DocumentSourcePopover>
                          ) : null}
                        </ActionRow>
                      )
                    }

                    return (
                      <ActionRow>
                        {setObject && (
                          <ExternalValueLookup
                            field={fullKey}
                            setValue={setObject}
                            onSourceChange={(newValue, newSource) => {
                              setVirtualObject?.((prev) => ({
                                ...prev,
                                [fullKey]: wrapFieldValue(newValue, newSource),
                              }))
                            }}
                          />
                        )}
                        {setObject && hasIncludeSource ? (
                          <DocumentSourcePopover source={source}>
                            <SourceInput
                              source={source}
                              onSourceChange={(newSource) => {
                                setVirtualObject?.((prev) => ({
                                  ...prev,
                                  [fullKey]: wrapFieldValue(object, newSource),
                                }))
                              }}
                            />
                          </DocumentSourcePopover>
                        ) : null}
                      </ActionRow>
                    )
                  }}
                />
              </UnextractedWrapper>
            </Tooltip>
          </CommentIndicator>
        )
      })}
    </SectionContainer>
  )
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const ActionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex: 1 1 auto;
  min-width: 0;
`

/**
 * Forces the LEAF INPUT itself to a fixed width so columns line up across
 * field types, while leaving room in `LeafActionsContainer` (rendered by
 * RecursivePropertyView right of the input) for the inline `Source:` picker
 * and citation chip. Scoped CSS -- only Ant inputs inside this PropertyView
 * are constrained; consumers of `RecursivePropertyView` outside ConditionalForm
 * are unaffected. Boolean checkbox is intentionally not matched.
 *
 * `!important` is required because `EnumFieldInput` sets an inline `width: 100%`
 * on its `<Select>` (and inline styles otherwise outrank CSS), and we need
 * every input type to render at exactly 12rem regardless of the inline width
 * it ships with.
 */
const FormFieldPropertyView = styled(RecursivePropertyView)`
  width: 100%;

  & .ant-input-number,
  & .ant-picker,
  & .ant-select,
  & .ant-input:not([type='checkbox']) {
    width: 12rem !important;
    flex: 0 0 12rem;
  }
`

const UnextractedWrapper = styled.div<{ $unextracted: boolean }>`
  ${({ $unextracted }) =>
    $unextracted &&
    `
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-select-selector,
    .ant-picker,
    input,
    textarea {
      border-color: #faad14 !important;
      box-shadow: 0 0 0 2px rgba(250, 173, 20, 0.2) !important;
    }
  `}
`
